import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { useFormik } from 'formik';
import {
  Assignment,
  Language,
  ListAlt,
  TipsAndUpdatesTwoTone,
  Comment,
} from '@mui/icons-material';
import { Box, FormControl, RadioGroup, Typography } from '@mui/material';
import { string, object } from 'yup';
import { programmeOptions } from './options';
import { getProgrammeType, type AdmissionRequirementBoxProps } from './types';
import { PAGE_TEXT } from '../../lang';
import { ProgrammeTypes, OfferSuggestion } from '../../../constants';
import {
  useUpdateNomination,
  type AdmissionRequirementValue,
} from '../../../graphql/NominationForm';
import {
  IconTitle,
  Select,
  TextField,
  Radio,
  Button,
  Accordion,
  Alert,
  type AlertDetails,
  Dialog,
  type SelectOption,
} from '../../../../../components';

const pageText = PAGE_TEXT.GSB1_FORM;

const entranceText = pageText.ENTRANCE_REQUIREMENT_TYPE;
const languageText = pageText.LANGUAGE_REQUIREMENT_TYPE;

const entranceRequirement: string[][] = [
  [
    entranceText.SECOND_CLASS_BACHELOR,
    entranceText.EQUAL_SECOND_CLASS_BACHELOR,
  ],
  [entranceText.MPHIL, entranceText.DISSERTATION_MASTER],
  [
    entranceText.FIRST_CLASS_BACHELOR,
    entranceText.EQUAL_FIRST_CLASS_BACHELOR,
    entranceText.MASTER,
  ],
];
const languageRequirement: string[] = [
  languageText.ENGLISH_TEST_RESULT,
  languageText.LANGUEGE_PROOF,
  pageText.NOT_MEET_REQUIREMENT,
];

const programmeTypeToEntranceActiveIdMap: { [key in ProgrammeTypes]: number } =
  {
    [ProgrammeTypes.TWO_YEAR_FULL_TIME_MPHIL]: 0,
    [ProgrammeTypes.FOUR_YEAR_PART_TIME_MPHIL]: 0,
    [ProgrammeTypes.THREE_YEAR_FULL_TIME_PHD]: 1,
    [ProgrammeTypes.SIX_YEAR_PART_TIME_PHD]: 1,
    [ProgrammeTypes.FOUR_YEAR_FULL_TIME_PHD]: 2,
    [ProgrammeTypes.EIGHT_YEAR_PART_TIME_PHD]: 2,
  };

export const AdmissionRequirementBox: React.FunctionComponent<
  AdmissionRequirementBoxProps
> = (props: AdmissionRequirementBoxProps) => {
  const {
    admission,
    isFullTime,
    isPhd,
    isViewMode,
    refetchAdmission,
    preliminaryCheck,
  } = props;
  const suggestProgramme: ProgrammeTypes[] = preliminaryCheck.rules
    .filter(rule => rule.result !== OfferSuggestion.REJECT)
    .flatMap(rule => getProgrammeType(rule.programmeLevel, rule.duration))
    .filter(programme => programme !== undefined);

  const [entranceActiveId, setEntranceActiveId] = useState<number>(
    programmeTypeToEntranceActiveIdMap[admission.programme as ProgrammeTypes] ??
      0,
  );
  const { nominationId } = useParams();
  const [openDialog, setOpenDialog] = useState(false);
  const [openAlert, setOpenAlert] = useState(false);
  const [alertDetails, setAlertDetails] = useState<AlertDetails>({
    alertContent: '',
    alertType: 'error',
  });

  const filteredProgrammeOptions: SelectOption[] = programmeOptions.map(
    option => ({
      value: option.value,
      label: option.label,
      disabled:
        !String(option.value).includes(isFullTime ? 'FULL' : 'PART') ||
        !String(option.value).includes(isPhd ? 'PHD' : 'MPHIL'),
    }),
  );

  const updateNomination = useUpdateNomination(refetchAdmission);
  const handleValidationBeforeSubmission = async (
    value: AdmissionRequirementValue,
  ): Promise<void> => {
    const { programme } = value;

    if (!suggestProgramme.includes(programme as ProgrammeTypes)) {
      setOpenDialog(true);
      return;
    }
    await handleSubmit(value);
  };

  const handleSubmit = async (
    value: AdmissionRequirementValue,
  ): Promise<void> => {
    setOpenDialog(false);

    const { entranceRequirement, languageRequirement } = value;
    try {
      const { programme, entranceJustification, justification } = value;
      await updateNomination({
        variables: {
          id: nominationId,
          input: {
            admission: {
              programme,
              requirements: {
                generalEntranceRequirements: [
                  {
                    requirements: entranceRequirement,
                    fulfilled:
                      entranceRequirement !== pageText.NOT_MEET_REQUIREMENT,
                    justification: showEntranceJustification
                      ? entranceJustification
                      : null,
                  },
                ],
                englishLanguageRequirements: [
                  {
                    requirements: languageRequirement,
                    fulfilled:
                      languageRequirement !== pageText.NOT_MEET_REQUIREMENT,
                  },
                ],
                justification:
                  languageRequirement === pageText.NOT_MEET_REQUIREMENT ||
                  entranceRequirement === pageText.NOT_MEET_REQUIREMENT
                    ? justification
                    : null,
              },
            },
          },
        },
      });
      setAlertDetails({
        alertContent: `Saved Admission Requirement successfully.`,
        alertType: 'success',
      });
      setOpenAlert(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (e) {
      setAlertDetails({
        alertContent: `Error Occurs.${(e as Error).message}`,
        alertType: 'error',
      });
      setOpenAlert(true);
    }
  };

  const formik = useFormik({
    initialValues: admission,
    validationSchema: object({
      programme: string().required('Required'),
      entranceRequirement: string().required(),
      languageRequirement: string().required(),
      entranceJustification: string().notRequired(),
      justification: string().when(
        ['languageRequirement', 'entranceRequirement'],
        ([languageRequirement, entranceRequirement], schema) => {
          const required: boolean =
            languageRequirement === pageText.NOT_MEET_REQUIREMENT ||
            entranceRequirement === pageText.NOT_MEET_REQUIREMENT;
          return required ? schema.required('Required') : schema.notRequired();
        },
      ),
    }),
    onSubmit: handleValidationBeforeSubmission,
  });

  const handleChangeEntranceActiveId = (
    ProgrammeType: ProgrammeTypes,
  ): void => {
    const entranceActiveId = programmeTypeToEntranceActiveIdMap[ProgrammeType];
    setEntranceActiveId(entranceActiveId);
  };

  const handleProgrammeChange = async (
    event: React.ChangeEvent<{ value: unknown }>,
  ): Promise<void> => {
    handleChangeEntranceActiveId(event.target.value as ProgrammeTypes);
    await formik.setFieldValue('programme', event.target.value);
    await formik.setFieldValue('entranceRequirement', '');
    await formik.setFieldValue('languageRequirement', '');
    await formik.setFieldValue('entranceJustification', '');
  };

  const showJustification =
    formik.values.languageRequirement === pageText.NOT_MEET_REQUIREMENT ||
    formik.values.entranceRequirement === pageText.NOT_MEET_REQUIREMENT;
  const showEntranceJustification =
    formik.values.programme === ProgrammeTypes.EIGHT_YEAR_PART_TIME_PHD ||
    formik.values.programme === ProgrammeTypes.FOUR_YEAR_FULL_TIME_PHD;

  const handleCancelSubmit = (): void => {
    setOpenDialog(false);
  };

  const handleConfirmSubmit = async (): Promise<void> => {
    await handleSubmit(formik.values);
  };
  return (
    <Accordion
      className="w-full"
      title={pageText.SUBTITLE}
      defaultExpanded
      disableExpand
      details={
        <Container>
          <Dialog
            openDialog={openDialog}
            dialogTitle={pageText.SUBMIT_CONFIRM.TITLE}
            dialogAction={PAGE_TEXT.YES}
            cancelAction={PAGE_TEXT.CANCEL}
            maxWidth={'sm'}
            onDialogAction={handleConfirmSubmit}
            onDialogClose={handleCancelSubmit}
          >
            <Typography margin={'0rem 1.5rem 2rem'}>
              {pageText.SUBMIT_CONFIRM.CONTENT}
            </Typography>
          </Dialog>
          <form onSubmit={formik.handleSubmit}>
            <Box>
              <IconTitle
                icon={<TipsAndUpdatesTwoTone />}
                title={pageText.SUGGEST_OFFER}
              />
              {preliminaryCheck.rules.map((rule, index) => {
                const { programmeLevel, result, checkRule, duration } = rule;
                return (
                  <div key={index} className="px-2 leading-loose">
                    <h2>
                      {index + 1}
                      {'.'}&nbsp; &nbsp;
                      {duration}
                      {'-year'}&nbsp;{'Full-time'}
                      {`/ ${duration * 2}`}
                      {'-year'}&nbsp;{`Part-time`}&nbsp;
                      {programmeLevel}&nbsp;{'-'}&nbsp;
                      <span className="italic font-semibold">{result}</span>
                    </h2>
                    {checkRule.map((checkRule, index) => {
                      const { item, result } = checkRule;
                      return (
                        <div key={index} className="ml-4">
                          {item}&nbsp;
                          <span className="italic">{result}</span>
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </Box>
            <Box>
              <IconTitle
                icon={<Assignment />}
                title={pageText.OFFER_PROGRAMME}
              />
              <Select
                style={{ width: '50%', marginTop: '1rem' }}
                options={filteredProgrammeOptions}
                name="programme"
                disabled={isViewMode}
                value={formik.values.programme}
                onChange={handleProgrammeChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.programme && Boolean(formik.errors.programme)
                }
                helperText={formik.touched.programme && formik.errors.programme}
              />
            </Box>
            <Box>
              <IconTitle
                icon={<ListAlt />}
                title={pageText.ENTRANCE_REQUIREMENT}
              />
              <FormControl>
                <RadioGroup
                  name="entranceRequirement"
                  value={formik.values.entranceRequirement}
                  onChange={formik.handleChange}
                >
                  {[
                    ...entranceRequirement[entranceActiveId],
                    pageText.NOT_MEET_REQUIREMENT,
                  ].map((item: string, index: number) => {
                    return (
                      <Radio
                        key={index}
                        value={item}
                        checked={formik.values.entranceRequirement === item}
                        label={item}
                        disabled={isViewMode}
                        error={
                          Boolean(formik.touched.entranceRequirement) &&
                          Boolean(formik.errors.entranceRequirement)
                        }
                      />
                    );
                  })}
                </RadioGroup>
              </FormControl>
              <div className="italic mt-6">
                {showEntranceJustification && (
                  <>
                    {pageText.ENTRANCE_REQUIREMENT_JUSTIFICATION}
                    <TextField
                      disabled={isViewMode}
                      className="w-full !mt-4"
                      multiline
                      minRows={4}
                      name="entranceJustification"
                      value={formik.values.entranceJustification}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                    />
                  </>
                )}
              </div>
            </Box>
            <Box>
              <IconTitle
                icon={<Language />}
                title={pageText.LANGUAGE_REQUIREMENT}
              />
              <FormControl>
                <RadioGroup
                  name="languageRequirement"
                  value={formik.values.languageRequirement}
                  onChange={formik.handleChange}
                >
                  {languageRequirement.map((item: string, index: number) => {
                    return (
                      <Radio
                        key={index}
                        value={item}
                        checked={formik.values.languageRequirement === item}
                        label={item}
                        disabled={isViewMode}
                        error={
                          Boolean(formik.touched.languageRequirement) &&
                          Boolean(formik.errors.languageRequirement)
                        }
                      />
                    );
                  })}
                </RadioGroup>
              </FormControl>
            </Box>
            {showJustification && (
              <Box>
                <IconTitle icon={<Comment />} title={pageText.JUSTIFICATION} />
                <TextField
                  style={{ width: '100%' }}
                  multiline
                  minRows={4}
                  disabled={isViewMode}
                  name="justification"
                  value={formik.values.justification}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.justification &&
                    Boolean(formik.errors.justification)
                  }
                  helperText={
                    formik.touched.justification && formik.errors.justification
                  }
                />
              </Box>
            )}
            <div className="w-full justify-center flex py-4">
              {!isViewMode && (
                <Button variant="contained" color="primary" type="submit">
                  {PAGE_TEXT.SAVE}
                </Button>
              )}
            </div>
          </form>
          <Alert
            openAlert={openAlert}
            setOpenAlert={setOpenAlert}
            alertDetails={alertDetails}
          />
        </Container>
      }
    />
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding-bottom: 2rem;

  form {
    padding: 2rem;
  }

  .MuiFormGroup-root {
    padding: 0 1rem;
  }

  .MuiBox-root {
    margin-bottom: 3rem;
  }
`;
